import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OverlayPanel } from "primereact/overlaypanel";
import { CustomTable } from "../../componentes/table/CustomTable";
import { useExportToExcel } from "../../componentes/table/useExportToExcel";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import Button from "../../componentes/button/button";
import CardIndicador from "../../components/solicitacoes/Shared/CardIndicador";
import PrioridadeBadge from "../../components/solicitacoes/Shared/PrioridadeBadge";
import TempoBadge from "../../components/solicitacoes/Shared/TempoBadge";
import KanbanBoard from "../../components/solicitacoes/Kanban/KanbanBoard";
import KanbanCard from "../../components/solicitacoes/Kanban/KanbanCard";
import { useMinhasTarefas } from "../../hooks/solicitacoes/useMinhasTarefas";
import { useSolicitacoes } from "../../hooks/solicitacoes/useSolicitacoes";
import { useFiltrosPersistidos } from "../../hooks/solicitacoes/useFiltrosPersistidos";
import { useViewportWidth } from "../../hooks/useViewportWidth";
import { useAuth } from "../../context/authContext";
import { processoInfo, etapaLabel } from "../../utils/solicitacoes/processoRegistry";
import { humanizarEtapa, humanizarEnum, PRIORIDADE_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { formatarData, formatarMoeda } from "../../utils/solicitacoes/formatters";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import SolicitacaoService from "../../services/resource/solicitacaoService";

const DETALHES_EM_PARALELO = 4;
const INTERVALO_APLICAR_DETALHES_MS = 300;

// Enriquece cada tarefa com Filial/Prioridade/campos do formulário buscando o detalhe do processo.
// Com muitas tarefas, disparar uma chamada por tarefa de uma vez (e re-renderizar a tela a cada
// resposta) travava o navegador — aqui no máximo DETALHES_EM_PARALELO chamadas ficam abertas, cada id
// é buscado uma única vez e as respostas são aplicadas em lote.
function useEnriquecidas(tarefas) {
    const [porId, setPorId] = useState({});
    const solicitadosRef = useRef(new Set());
    const filaRef = useRef([]);
    const emAndamentoRef = useRef(0);
    const lotePendenteRef = useRef({});
    const timerLoteRef = useRef(null);
    const ativoRef = useRef(true);

    useEffect(() => () => {
        ativoRef.current = false;
        clearTimeout(timerLoteRef.current);
    }, []);

    function aplicarLote() {
        timerLoteRef.current = null;
        const lote = lotePendenteRef.current;
        lotePendenteRef.current = {};
        if (ativoRef.current && Object.keys(lote).length > 0) setPorId(prev => ({ ...prev, ...lote }));
    }

    function processarFila() {
        while (ativoRef.current && emAndamentoRef.current < DETALHES_EM_PARALELO && filaRef.current.length > 0) {
            const tarefa = filaRef.current.shift();
            emAndamentoRef.current += 1;
            processoInfo(tarefa.tipoProcesso).service.buscarDetalhe(tarefa.id)
                .then(res => {
                    lotePendenteRef.current[tarefa.id] = res.data;
                    if (!timerLoteRef.current) timerLoteRef.current = setTimeout(aplicarLote, INTERVALO_APLICAR_DETALHES_MS);
                })
                .catch(() => { })
                .finally(() => {
                    emAndamentoRef.current -= 1;
                    processarFila();
                });
        }
    }

    useEffect(() => {
        tarefas.forEach(t => {
            if (!processoInfo(t.tipoProcesso)?.temDetalhe || solicitadosRef.current.has(t.id)) return;
            solicitadosRef.current.add(t.id);
            filaRef.current.push(t);
        });
        processarFila();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tarefas]);

    return porId;
}

const CARDS_POR_LOTE_MOBILE = 30;

function celulaTruncada(valor, largura = 180) {
    if (!valor) return <span className="text-muted-foreground italic">—</span>;
    return (
        <span className="block truncate" style={{ maxWidth: largura }} title={valor}>
            {valor}
        </span>
    );
}

// Colunas extras exibidas na Tabela quando um único Processo está filtrado (mesmo requisito do
// Kanban) — cada uma lê do detalhe já buscado por useEnriquecidas (processo.service.buscarDetalhe),
// sem chamada adicional. Só os 2-3 campos mais representativos de cada formulário, não o objeto inteiro.
const COLUNAS_EXTRA_POR_PROCESSO = {
    LANCAMENTO_DESPESA: [
        { key: "extraFornecedor", label: "Fornecedor", campo: d => d?.fornecedorNome },
        { key: "extraTipo", label: "Tipo", campo: d => humanizarEnum(d?.tipoDespesa) },
        { key: "extraDescrição", label: "Descrição", campo: d => humanizarEnum(d?.descricao) },
        { key: "extraValor", label: "Valor", campo: d => d?.valorTotal, moeda: true },
        { key: "extraVencimento", label: "Vencimento", campo: d => d?.vencimento, data: true },
        { key: "extraPagamento", label: "Tipo de Pagamento", campo: d => d?.tipoPagamento },
    ],
    SOLICITACAO_COMPRAS: [
        { key: "extraFornecedor", label: "Fornecedor", campo: d => d?.fornecedorNome },
        { key: "extraTipoProduto", label: "Tipo de Produto", campo: d => d?.tipoProdutoNome },
        { key: "extraValor", label: "Valor", campo: d => d?.valorFinalCotacao ?? d?.valorCompra ?? d?.menorCotacao, moeda: true },
        { key: "extraFilialEntrega", label: "Filial de Entrega", campo: d => d?.filialEntregaNome },
        { key: "extraMotivo", label: "Motivo", campo: d => d?.motivo },
        { key: "extraVencimento", label: "Vencimento", campo: d => d?.vencimento || null },
    ],
    PROCESSO_MULTAS: [
        { key: "extraCondutor", label: "Condutor", campo: d => d?.condutorNome },
        { key: "extraVeiculo", label: "Veículo", campo: d => d?.veiculoPlaca },
        { key: "extraValor", label: "Valor da Multa", campo: d => d?.valorMulta, moeda: true },
    ],
    // Processo único (Promoção/Enquadramento/Transferência) — mostra o(s) tipo(s) de mudança
    // incluída(s) na solicitação, já que uma linha pode combinar qualquer um dos 3.
    PROMOCAO_FUNCIONARIOS: [
        { key: "extraFuncionario", label: "Funcionário", campo: d => d?.funcionarioNome },
        {
            key: "extraTipoMudanca", label: "Tipo de Mudança", campo: d => [
                d?.incluiPromocao && "Promoção", d?.incluiTransferencia && "Transferência", d?.incluiEnquadramento && "Enquadramento",
            ].filter(Boolean).join(", "),
        },
        { key: "extraValor", label: "Salário Proposto", campo: d => d?.salarioProposto, moeda: true },
    ],
    DESLIGAMENTO_PESSOAL: [
        { key: "extraFuncionario", label: "Funcionário", campo: d => d?.funcionarioNome },
        { key: "extraMotivo", label: "Motivo", campo: d => humanizarEnum(d?.motivoDesligamento) },
        { key: "extraData", label: "Data Prevista", campo: d => d?.dataPrevistaDesligamento, data: true },
    ],
    SOLICITACAO_PESSOAL: [
        { key: "extraFilial", label: "Filial", campo: d => d?.unidadeNome },
        { key: "extraCargo", label: "Cargo a Preencher", campo: d => d?.cargoAPreencher },
        { key: "extraMotivo", label: "Motivo", campo: d => humanizarEnum(d?.motivoVaga) },
    ],
    ABERTURA_CHAMADO_TI: [
        { key: "extraResumo", label: "Resumo", campo: d => d?.resumo },
        { key: "extraTipoServico", label: "Tipo de Serviço", campo: d => d?.tipoServicoNome },
    ],
    RECOLHIMENTO_ATIVOS: [
        { key: "extraCliente", label: "Cliente", campo: d => d?.clienteRazaoSocial },
        { key: "extraFilial", label: "Filial", campo: d => d?.filialNome },
        { key: "extraMotivo", label: "Motivo", campo: d => d?.motivo },
    ],
    SOLICITACAO_ATIVOS: [
        { key: "extraCliente", label: "Cliente", campo: d => d?.clienteRazaoSocial },
        { key: "extraFilial", label: "Filial", campo: d => d?.filialNome },
        { key: "extraData", label: "Data Prevista Entrega", campo: d => d?.dataPrevistaEntrega, data: true },
    ],
    ANALISE_LIMITE_PRAZO: [
        { key: "extraCliente", label: "Cliente", campo: d => d?.clienteRazaoSocial },
        { key: "extraTipo", label: "Tipo", campo: d => humanizarEnum(d?.tipoSolicitacao) },
        { key: "extraValor", label: "Limite Proposto", campo: d => d?.limiteProposto, moeda: true },
    ],
    CADASTRO_CLIENTE: [
        { key: "extraCliente", label: "Cliente", campo: d => d?.nomeCliente },
        { key: "extraCodigo", label: "Código", campo: d => d?.clienteCodigo },
        { key: "extraTipo", label: "Tipo", campo: d => humanizarEnum(d?.tipoSolicitacao) },
        { key: "extraCidade", label: "Cidade", campo: d => d?.cidade },
        { key: "extraTelefone", label: "Telefone", campo: d => d?.telefone1 },
    ],
    RENOVACAO_GDM: [
        { key: "extraCliente", label: "Cliente", campo: d => d?.clienteRazaoSocial },
        { key: "extraCodigo", label: "Código", campo: d => d?.clienteCodigo },
        { key: "extraSerie", label: "Nº de Série", campo: d => d?.gdm?.numeroSerie },
        { key: "extraGdm", label: "GDM", campo: d => d?.gdm?.gdmDescricao },
    ],
};

function BotaoAcaoLinha({ icone, titulo, onClick, variante = "outline" }) {
    const cores = {
        outline: "text-muted-foreground hover:text-primary hover:bg-primary/10",
        primary: "text-primary hover:bg-primary/10",
    };
    return (
        <button
            type="button"
            title={titulo}
            onClick={onClick}
            className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors duration-150 ${cores[variante]}`}
        >
            <i className={icone} />
        </button>
    );
}

function CampoFiltro({ label, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground/70">{label}</label>
            {children}
        </div>
    );
}

const FILTROS_INICIAIS = {
    busca: "", processo: null, filial: null, etapa: null, prioridade: null, responsavel: null,
    mostrarConcluidas: false,
};

const ABAS = [
    { chave: "comigo", label: "Tarefas para concluir", icone: "fa fa-inbox", iconClassName: "bg-primary/10 text-primary" },
    { chave: "grupo", label: "Do meu grupo", icone: "fa fa-users", iconClassName: "bg-orange-100 text-orange-600" },
    { chave: "minhas", label: "Minhas solicitações", icone: "fa fa-list-alt", iconClassName: "bg-success/10 text-success" },
];

export default function MinhasTarefas() {
    const navigate = useNavigate();

    const solicitacaoService = new SolicitacaoService();


    const { user } = useAuth();
    const { pendentesComigo, pendentesDoMeuGrupo, loading: carregandoTarefas, recarregar: recarregarTarefas } = useMinhasTarefas();
    const { loading: carregandoSolicitacoes, recarregar: recarregarSolicitacoes } = useSolicitacoes();
    const { exportToExcel } = useExportToExcel();
    const [solicitacoes, setSolicitacoes] = useState([]);
    const largura = useViewportWidth();

    const [aba, setAba] = useState("comigo");
    const [grupoSelecionado, setGrupoSelecionado] = useState(null);
    const [visao, setVisao] = useState("kanban");
    const [filtros, setFiltros, limparFiltrosBase] = useFiltrosPersistidos("solicitacoes.minhasTarefas.filtros", FILTROS_INICIAIS);
    const grupoOverlayRef = useRef(null);

    function selecionarAba(chave, evento) {
        if (chave !== "grupo") setGrupoSelecionado(null);
        setAba(chave);
        setLimiteMobile(CARDS_POR_LOTE_MOBILE);
        if (chave === "grupo") grupoOverlayRef.current?.toggle(evento);
    }

    function selecionarGrupo(grupoId) {
        setGrupoSelecionado(grupoId);
        grupoOverlayRef.current?.hide();
    }

    useEffect(() => {
        document.title = "MyDash | Central de Tarefas";

        solicitacaoService.listarMinhasSolicitacoes()
            .then(res => setSolicitacoes(res.data))
            .catch(error => {
                console.error("Erro ao listar minhas solicitações", error);
            });
    }, []);


    const minhasSolicitacoes = useMemo(
        () => solicitacoes.filter(s => s.solicitanteId === user?.id),
        [solicitacoes, user]
    );

    // Agrupa as pendências do grupo (já é a lista completa e correta vinda de GET /pendentes,
    // filtrada só por "sem responsável individual + tem grupo") por grupoResponsavelAtualId, sem
    // precisar de um endpoint dedicado — os dados já são exatamente os que a etapa 2 vai usar.
    const gruposComPendencias = useMemo(() => {
        const porGrupo = new Map();
        pendentesDoMeuGrupo.forEach(t => {
            const id = t.grupoResponsavelAtualId;
            if (id == null) return;
            if (!porGrupo.has(id)) {
                porGrupo.set(id, { grupoId: id, grupoNome: t.grupoResponsavelAtualNome || "Grupo", quantidade: 0, etapas: new Map() });
            }
            const grupo = porGrupo.get(id);
            grupo.quantidade += 1;
            const etapaTxt = t.etapaAtual ? (etapaLabel(t.tipoProcesso, t.etapaAtual) || humanizarEtapa(t.etapaAtual)) : "Encerrada";
            grupo.etapas.set(etapaTxt, (grupo.etapas.get(etapaTxt) || 0) + 1);
        });
        return Array.from(porGrupo.values())
            .map(g => ({ ...g, porEtapa: Array.from(g.etapas, ([etapa, quantidade]) => ({ etapa, quantidade })) }))
            .sort((a, b) => b.quantidade - a.quantidade);
    }, [pendentesDoMeuGrupo]);

    // Só lista tarefas depois que um grupo específico é escolhido no dropdown — sem seleção, a aba
    // fica vazia (não mistura as pendências de todos os grupos numa lista só).
    const tarefasDoMeuGrupo = useMemo(
        () => grupoSelecionado ? pendentesDoMeuGrupo.filter(t => t.grupoResponsavelAtualId === grupoSelecionado) : [],
        [pendentesDoMeuGrupo, grupoSelecionado]
    );

    const dadosPorAba = {
        comigo: pendentesComigo,
        grupo: tarefasDoMeuGrupo,
        minhas: minhasSolicitacoes,
    };
    const tarefasAba = dadosPorAba[aba];
    const carregando = carregandoTarefas || carregandoSolicitacoes;
    // Em "Minhas solicitações", as concluídas/canceladas ficam escondidas por padrão — não busca o
    // detalhe delas enquanto não forem exibidas (costumam ser a maior parte da lista).
    const tarefasParaEnriquecer = useMemo(
        () => aba === "minhas" && !filtros.mostrarConcluidas
            ? tarefasAba.filter(t => t.status !== "FINALIZADA" && t.status !== "CANCELADA")
            : tarefasAba,
        [tarefasAba, aba, filtros.mostrarConcluidas]
    );
    const detalhesPorId = useEnriquecidas(tarefasParaEnriquecer);
    const [limiteMobile, setLimiteMobile] = useState(CARDS_POR_LOTE_MOBILE);

    function set(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    function limparFiltros() {
        limparFiltrosBase();
    }

    function atualizar() {
        recarregarTarefas();
        recarregarSolicitacoes();
    }

    const linhasBase = useMemo(() => tarefasAba.map(t => {
        const detalhe = detalhesPorId[t.id];
        // Campos extras já calculados aqui (não só no render das colunas) para que o filtro/ordenação
        // genéricos da CustomTable (que leem row[col.key] direto) enxerguem um valor de verdade.
        const extras = {};
        (COLUNAS_EXTRA_POR_PROCESSO[t.tipoProcesso] || []).forEach(def => {
            extras[def.key] = def.campo(detalhe) ?? null;
        });
        return {
            ...t,
            processoLabel: processoInfo(t.tipoProcesso)?.label || t.tipoProcesso,
            etapaLabelTxt: t.etapaAtual ? (etapaLabel(t.tipoProcesso, t.etapaAtual) || humanizarEtapa(t.etapaAtual)) : "Encerrada",
            filialNome: detalhe?.filialNome || "-",
            vencimento: detalhe?.vencimento || null,
            prioridade: detalhe?.prioridade || null,
            responsavelTxt: t.responsavelAtualNome || t.grupoResponsavelAtualNome || "-",
            ...extras,
        };
    }), [tarefasAba, detalhesPorId]);

    // Ordenado do processo com mais tarefas pra menos — o clique único (chips) prioriza o que
    // provavelmente importa mais agora, em vez da ordem alfabética de um dropdown.
    const opcoesProcesso = useMemo(() => {
        const porTipo = new Map();
        linhasBase.forEach(l => {
            const atual = porTipo.get(l.tipoProcesso);
            if (atual) atual.quantidade += 1;
            else porTipo.set(l.tipoProcesso, { value: l.tipoProcesso, label: l.processoLabel, icone: processoInfo(l.tipoProcesso)?.icone, quantidade: 1 });
        });
        return Array.from(porTipo.values()).sort((a, b) => b.quantidade - a.quantidade);
    }, [linhasBase]);

    const opcoesFilial = useMemo(() => {
        const vistos = new Set(linhasBase.map(l => l.filialNome).filter(v => v && v !== "-"));
        return Array.from(vistos).sort().map(v => ({ value: v, label: v }));
    }, [linhasBase]);

    const opcoesEtapa = useMemo(() => {
        const vistos = new Map();
        linhasBase.forEach(l => { if (l.etapaAtual) vistos.set(l.etapaAtual, l.etapaLabelTxt); });
        return Array.from(vistos, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    }, [linhasBase]);

    const opcoesResponsavel = useMemo(() => {
        const vistos = new Set(linhasBase.map(l => l.responsavelTxt).filter(v => v && v !== "-"));
        return Array.from(vistos).sort().map(v => ({ value: v, label: v }));
    }, [linhasBase]);

    const opcoesPrioridade = Object.entries(PRIORIDADE_CONFIG).map(([value, info]) => ({ value, label: info.label }));

    const temFiltroAtivo = Object.keys(FILTROS_INICIAIS).some(chave => filtros[chave] !== FILTROS_INICIAIS[chave]);

    const linhas = useMemo(() => linhasBase.filter(l => {
        if (filtros.busca) {
            const termo = filtros.busca.toLowerCase();
            const alvo = `${l.numero} ${l.solicitanteNome || ""}`.toLowerCase();
            if (!alvo.includes(termo)) return false;
        }
        if (filtros.processo && l.tipoProcesso !== filtros.processo) return false;
        if (filtros.filial && l.filialNome !== filtros.filial) return false;
        if (filtros.etapa && l.etapaAtual !== filtros.etapa) return false;
        if (filtros.prioridade && l.prioridade !== filtros.prioridade) return false;
        if (filtros.responsavel && l.responsavelTxt !== filtros.responsavel) return false;
        if (aba === "minhas" && !filtros.mostrarConcluidas && (l.status === "FINALIZADA" || l.status === "CANCELADA")) return false;
        return true;
    }), [linhasBase, filtros, aba]);

    const isMobile = largura < 768;
    const isTablet = largura >= 768 && largura < 1024;

    // Kanban só faz sentido com um único Processo filtrado (precisa das etapas dele para as colunas)
    // — dentro dessa condição, o usuário escolhe explicitamente entre Kanban ou Tabela.
    const podeExibirKanban = !!filtros.processo;
    const modoKanban = podeExibirKanban && visao === "kanban";
    const etapasDoProcesso = modoKanban ? processoInfo(filtros.processo)?.etapas || [] : [];

    // Com um Processo filtrado e a Tabela escolhida, acrescenta as colunas mais representativas do
    // formulário daquele processo (o mesmo detalhe já buscado por useEnriquecidas, sem nova chamada).
    const colunasExtras = useMemo(() => {
        if (modoKanban || !filtros.processo) return [];
        const definicoes = COLUNAS_EXTRA_POR_PROCESSO[filtros.processo] || [];
        return definicoes.map(def => ({
            key: def.key,
            label: def.label,
            // "format" aqui só orienta o dropdown de filtro (useTableLogic) a formatar as opções
            // (moeda/data) — a célula em si sempre usa o "render" abaixo, que tem prioridade.
            format: def.moeda ? "currency" : def.data ? "date" : undefined,
            // Valor já vem pronto de linhasBase (row[def.key]) — filtro/ordenação da CustomTable
            // funcionam nativamente porque não dependem mais só do render.
            render: v => {
                if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
                if (def.moeda) return <span className="font-medium text-foreground">{formatarMoeda(v)}</span>;
                if (def.data) return formatarData(v);
                return celulaTruncada(v, 150);
            },
        }));
    }, [filtros.processo, modoKanban]);

    const colunasCompletas = useMemo(() => [
        { key: "numero", label: "Número", width: 110, stickyLeft: true },
        { key: "processoLabel", label: "Processo", render: v => celulaTruncada(v, 160) },
        { key: "solicitanteNome", label: "Solicitante", render: v => celulaTruncada(v, 160) },
        { key: "filialNome", label: "Filial", render: v => celulaTruncada(v, 140) },
        { key: "etapaLabelTxt", label: "Etapa Atual", render: v => celulaTruncada(v, 170) },
        { key: "responsavelTxt", label: "Responsável", render: v => celulaTruncada(v, 150) },
        { key: "prioridade", label: "Prioridade", center: true, render: v => v ? <PrioridadeBadge prioridade={v} /> : <span className="text-muted-foreground">—</span> },
        { key: "vencimento", label: "Vencimento Pgto.", format: "date" },
        ...colunasExtras,
        { key: "dataAtualizacao", label: "Tempo na etapa", center: true, sortable: false, filterable: false, render: (v, row) => <TempoBadge data={row.dataAtualizacao} /> },
        { key: "dataCriacao", label: "Data abertura", format: "date" },
        { key: "dataAtualizacaoTxt", label: "Última atualização", sortable: false, filterable: false, render: (v, row) => formatarData(row.dataAtualizacao) },
        {
            key: "acoes", label: "Ações", sortable: false, filterable: false, center: true, noSelected: true,
            render: (v, row) => (
                <div className="flex items-center justify-center gap-1">
                    <BotaoAcaoLinha icone="fa fa-eye" titulo="Abrir" variante="primary" onClick={e => navegarComCtrl(navigate, `/solicitacoes/${row.id}`, e)} />
                </div>
            ),
        },
    ], [navigate, colunasExtras]);

    const chavesTablet = new Set(["numero", "processoLabel", "solicitanteNome", "etapaLabelTxt", "prioridade", "dataAtualizacao", "dataCriacao", "acoes"]);
    const colunas = isTablet ? colunasCompletas.filter(c => chavesTablet.has(c.key)) : colunasCompletas;

    function exportar() {
        exportToExcel(linhas, colunasCompletas.filter(c => c.key !== "acoes"), "minhas-tarefas");
    }

    return (
        <main className="px-4 py-6 md:py-8">
            <div className="mx-auto flex flex-col gap-6">
                <div>
                    <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2.5">
                        <i className="fa fa-tasks text-primary" /> Central de Tarefas
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Acompanhe todas as tarefas pendentes e solicitações em um único lugar.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <CardIndicador
                        label="Tarefas para concluir"
                        value={pendentesComigo.length}
                        icon="fa fa-inbox"
                        iconClassName="bg-primary/10 text-primary"
                        loading={carregandoTarefas}
                        active={aba === "comigo"}
                        onClick={() => selecionarAba("comigo")}
                    />
                    <CardIndicador
                        label="Tarefas do meu grupo"
                        value={pendentesDoMeuGrupo.length}
                        icon="fa fa-users"
                        iconClassName="bg-orange-100 text-orange-600"
                        loading={carregandoTarefas}
                        active={aba === "grupo"}
                        onClick={e => selecionarAba("grupo", e)}
                    />
                    <CardIndicador
                        label="Minhas solicitações"
                        value={minhasSolicitacoes.length}
                        icon="fa fa-clipboard-list"
                        iconClassName="bg-success/10 text-success"
                        loading={carregandoSolicitacoes}
                        active={aba === "minhas"}
                        onClick={() => selecionarAba("minhas")}
                    />
                </div>

                <div className="bg-card border border-border rounded-lg shadow-card p-4 md:p-5">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <i className="fa fa-filter text-muted-foreground" /> Filtros
                        </h2>
                        <div className="flex items-center gap-2">
                            {temFiltroAtivo && (
                                <Button variant="outline" label="Limpar filtros" iconLeft="fa fa-eraser" onClick={limparFiltros} />
                            )}
                            <Button variant="blueLight" label="Atualizar" iconLeft="fa fa-rotate" loading={carregando} onClick={atualizar} />
                        </div>
                    </div>

                    {opcoesProcesso.length > 0 && (
                        <div className="flex flex-col gap-1.5 mb-4">
                            <label className="text-xs font-medium text-foreground/70">Processo</label>
                            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
                                <button
                                    type="button"
                                    onClick={() => set("processo", null)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold whitespace-nowrap transition-colors duration-150 ${!filtros.processo ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                                >
                                    Todos
                                </button>
                                {opcoesProcesso.map(op => {
                                    const ativo = filtros.processo === op.value;
                                    return (
                                        <button
                                            key={op.value}
                                            type="button"
                                            title={op.label}
                                            onClick={() => set("processo", ativo ? null : op.value)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold whitespace-nowrap transition-colors duration-150 ${ativo ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                                        >
                                            {op.icone && <i className={op.icone} />}
                                            {op.label}
                                            <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${ativo ? "bg-white/20" : "bg-muted text-muted-foreground"}`}>
                                                {op.quantidade}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                        <CampoFiltro label="Pesquisar">
                            <div className="relative">
                                <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground" />
                                <input
                                    type="text"
                                    value={filtros.busca}
                                    onChange={e => set("busca", e.target.value)}
                                    placeholder="Processo, número ou solicitante"
                                    className="w-full h-9 pl-8 pr-3 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                                />
                            </div>
                        </CampoFiltro>
                        <CampoFiltro label="Filial">
                            <CustomDropdown value={filtros.filial} onChange={e => set("filial", e.value)} options={opcoesFilial} placeholder="Todas" showClear filter />
                        </CampoFiltro>
                        <CampoFiltro label="Etapa">
                            <CustomDropdown value={filtros.etapa} onChange={e => set("etapa", e.value)} options={opcoesEtapa} placeholder="Todas" showClear filter />
                        </CampoFiltro>
                        <CampoFiltro label="Prioridade">
                            <CustomDropdown value={filtros.prioridade} onChange={e => set("prioridade", e.value)} options={opcoesPrioridade} placeholder="Todas" showClear />
                        </CampoFiltro>
                        <CampoFiltro label="Responsável">
                            <CustomDropdown value={filtros.responsavel} onChange={e => set("responsavel", e.value)} options={opcoesResponsavel} placeholder="Todos" showClear filter />
                        </CampoFiltro>
                    </div>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        {window.innerWidth > 768 &&
                            <div className="flex items-center gap-1.5 overflow-x-auto">
                                {ABAS.map(a => (
                                    <button
                                        key={a.chave}
                                        onClick={e => selecionarAba(a.chave, e)}
                                        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border-b-2 transition-all duration-150 whitespace-nowrap ${aba === a.chave
                                            ? "border-primary text-primary bg-primary/5"
                                            : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
                                            }`}
                                    >
                                        <i className={a.icone} />
                                        {a.label}
                                        {a.chave === "grupo" && <i className="fa fa-chevron-down text-[10px] opacity-70" />}
                                        <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${aba === a.chave ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                                            {a.chave === "grupo" ? pendentesDoMeuGrupo.length : dadosPorAba[a.chave].length}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        }

                        {podeExibirKanban && (
                            <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
                                <button
                                    type="button"
                                    onClick={() => setVisao("kanban")}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors duration-150 ${visao === "kanban" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                                >
                                    <i className="fa fa-columns" /> Kanban
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setVisao("tabela")}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors duration-150 ${visao === "tabela" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                                >
                                    <i className="fa fa-table" /> Tabela
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="blue" label="Nova Solicitação" iconLeft="fa fa-plus" onClick={() => navigate("/solicitacoes/nova")} />
                        <Button variant="outline" iconLeft="fa fa-rotate" title="Atualizar" loading={carregando} onClick={atualizar} />
                        <Button variant="outline" iconLeft="fa fa-file-export" title="Exportar" onClick={exportar} />
                    </div>
                </div>

                <OverlayPanel ref={grupoOverlayRef} className="max-w-xs">
                    <div className="min-w-[260px] p-1 flex flex-col gap-1">
                        {gruposComPendencias.length === 0 ? (
                            <p className="text-xs text-muted-foreground px-2 py-3">Nenhum grupo com tarefas pendentes.</p>
                        ) : (
                            gruposComPendencias.map(g => (
                                <button
                                    key={g.grupoId}
                                    type="button"
                                    onClick={() => selecionarGrupo(g.grupoId)}
                                    className={`flex flex-col gap-0.5 px-2 py-2 rounded-md text-left transition-colors duration-150 ${grupoSelecionado === g.grupoId ? "bg-primary/10" : "hover:bg-muted"}`}
                                >
                                    <span className="flex items-center justify-between gap-3">
                                        <span className={`text-sm truncate ${grupoSelecionado === g.grupoId ? "text-primary font-semibold" : "text-foreground"}`}>{g.grupoNome}</span>
                                        <span className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold shrink-0 ${grupoSelecionado === g.grupoId ? "bg-primary text-primary-foreground" : "bg-slate-200 text-foreground"}`}>
                                            {g.quantidade}
                                        </span>
                                    </span>

                                </button>
                            ))
                        )}
                    </div>
                </OverlayPanel>

                {aba === "grupo" && !grupoSelecionado ? (
                    <div className="flex flex-col items-center gap-2 text-center py-16 border border-dashed border-border rounded-lg">
                        <i className="fa fa-users text-3xl text-muted-foreground/60" />
                        <p className="text-sm text-muted-foreground">
                            Selecione um grupo em "Do meu grupo" acima para ver as tarefas pendentes.
                        </p>
                    </div>
                ) : modoKanban ? (
                    <>
                        <p className="text-xs text-muted-foreground -mt-2">
                            <i className="fa fa-info-circle mr-1" />
                            Exibindo em quadro Kanban por etapa (filtro de processo ativo). Ctrl/Cmd+clique num card abre em nova aba.
                        </p>
                        <KanbanBoard etapas={etapasDoProcesso} solicitacoes={linhas} detalhesPorId={detalhesPorId} />
                    </>
                ) : (
                    <div className="border border-border rounded-lg bg-white shadow-card">
                        {isMobile ? (
                            <div className="flex flex-col gap-3 p-3">
                                {carregando ? (
                                    <p className="text-sm text-muted-foreground text-center py-8">Carregando...</p>
                                ) : linhas.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-8">Nenhum registro encontrado</p>
                                ) : (
                                    <>
                                        {linhas.slice(0, limiteMobile).map(row => (
                                            <KanbanCard
                                                key={row.id}
                                                solicitacao={row}
                                                detalhe={detalhesPorId[row.id]}
                                                autoDetalhe={false}
                                                onClick={e => navegarComCtrl(navigate, `/solicitacoes/${row.id}`, e)}
                                            />
                                        ))}
                                        {linhas.length > limiteMobile && (
                                            <Button
                                                variant="outline"
                                                label={`Mostrar mais (${linhas.length - limiteMobile} restantes)`}
                                                onClick={() => setLimiteMobile(l => l + CARDS_POR_LOTE_MOBILE)}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <CustomTable
                                showHeader={false}
                                data={linhas}
                                columns={colunas}
                                loading={carregando}
                                pagination
                                className="text-xs"
                                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.id}`, e)}
                            />
                        )}
                    </div>
                )}
            </div>
        </main>
    );
}
